// Drilldown for one Cashflow Reconciliation Check mismatch (see
// cashflow_reconciliation_check.py::build_cashflow_reconciliation_detail()):
// our CashFlows rows vs. the platform's own transaction/register rows for
// one investment_id, side by side. `our_rows`/`platform_rows` are generic
// {columns: string[], rows: any[][]} tables (column sets differ per
// platform - Finbee/Savy/Mozzeno/Vauraus each read a different file/sheet),
// so columns are rendered dynamically by position rather than by name.

import React, { useEffect, useState, useCallback } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingState from '../../common/LoadingState';
import errorMessage from '../../../helpers/errorMessage';

const toRowObjects = (table) => {
  if (!table) return [];
  return table.rows.map((row, i) => {
    const obj = { _key: i };
    table.columns.forEach((c, ci) => {
      obj[`c${ci}`] = row[ci];
    });
    return obj;
  });
};

const GenericTable = ({ title, table }) => {
  if (!table) return null;
  const rows = toRowObjects(table);
  return (
    <div>
      <h5 className="cr-subsection-title">{title}</h5>
      {rows.length === 0 ? (
        <p className="cr-muted-text">No rows.</p>
      ) : (
        // Standard table layout like the other wizard tables: PrimeReact 8's
        // `scrollable` mode lays rows out as flex rows, so header and body
        // cells size independently and drift out of line.
        <DataTable value={rows} dataKey="_key" size="small" showGridlines stripedRows paginator rows={10} responsiveLayout="scroll">
          {table.columns.map((c, ci) => (
            <Column key={ci} field={`c${ci}`} header={c} body={(r) => String(r[`c${ci}`] ?? '')} />
          ))}
        </DataTable>
      )}
    </div>
  );
};

const CashflowReconciliationDetailDialog = ({ reportingId, visible, platform, investmentId, onHide }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!reportingId || !visible || !platform || !investmentId) return;
    setLoading(true);
    setDetail(null);
    try {
      const res = await axiosWithAuth.get(`/compartment-report/reportings/${reportingId}/cashflow-reconciliation-check/detail`, {
        params: { platform, investment_id: investmentId },
      });
      setDetail(res.data);
      setError(null);
    } catch (err) {
      console.error('[CashflowReconciliationDetailDialog] fetch error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to load reconciliation detail.'));
    } finally {
      setLoading(false);
    }
  }, [reportingId, visible, platform, investmentId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return (
    <Dialog
      header={`Cashflow Reconciliation Detail - ${platform || ''} / ${investmentId || ''}`}
      visible={visible}
      onHide={onHide}
      style={{ width: '80rem', maxWidth: '95vw' }}
      maximizable
    >
      {loading && <LoadingState label="Loading reconciliation detail" />}
      {error && <div className="ex-inline-error">{error}</div>}
      {detail && (
        <>
          <div className="cr-meta-block">
            <div>{detail.comparison}</div>
            <div>
              Our Sum: {detail.our_amount.toLocaleString('en-IE', { minimumFractionDigits: 2 })} - Originator Sum:{' '}
              {detail.platform_amount.toLocaleString('en-IE', { minimumFractionDigits: 2 })} - Diff:{' '}
              {detail.diff.toLocaleString('en-IE', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="flex flex-column gap-4">
            <GenericTable title="Our CashFlows" table={detail.our_rows} />
            <GenericTable title={`${platform} Rows`} table={detail.platform_rows} />
          </div>
        </>
      )}
    </Dialog>
  );
};

export default CashflowReconciliationDetailDialog;
